import { CatalogueListProps } from '@engyalo/delivery-ui-components/lib/ui/components/CatalogueList'
import { DefaultProps } from '../../../interfaces'
import {
  CinnamonActionNames,
  CinnamonPaths,
  TemplateActionNames,
  TextLibrary,
} from '../defaultConstants'
import { ItemCardCatalog, ItemCardCatalogMultiImages } from './ItemCard'
import deepMerge from '../../../utils/deepMerge'
import { ItemField } from '@engyalo/delivery-ui-components/lib/interfaces/item'

export const CatalogueListBase: DefaultProps<CatalogueListProps> = {
  catalogue: '{{catalog.categories}}' as any,
  isFetchingMore: '{{catalog.isFetchingMore}}' as any,
  loadingMoreElementsText: TextLibrary.LOADING_MORE_ELEMENTS_TEXT,
  itemCard: {
    ...ItemCardCatalog,
    maxQtyMessage: TextLibrary.CART_MAX_QTY_ALERT,
    // Re-renders the rows while `refreshCatalog` writes new prices; see
    // `ICatalog.repricing` (COM-1500). Also defeats `memoizeItems`, which
    // treats `itemCard.noMemoize` as "always re-render", for that one window.
    noMemoize: '{{catalog.repricing}}' as any,
    image: {
      ...ItemCardCatalog.image,
      dimensions: '{{globals.imageConfig.imageSizes.catalogueImage}}' as any,
    },
  },
  layout: '{{globals.catalog.layout}}' as any,
  // Binding a prop the installed delivery-ui-components does not consume is
  // not inert. This resolves on every instance whatever its value, so against
  // a version that does not destructure `memoizeItems` it reaches the
  // component's rest props, and nothing between there and the DOM drops it:
  // `ItemList` spreads its rest onto `ItemListWrapper` (`styled(Grid)`), the
  // library's `Grid` spreads its own rest onto `GridWrapper` (`styled(Box)` —
  // MUI's), and no link declares `shouldForwardProp`. So `Box` puts it on the
  // element and every catalogue list in the fleet gets a React unknown-prop
  // warning, on storefronts that never opted in.
  //
  // So the pin in packages/cinnamon/package.json has to lead: 1.19.1 is the
  // first version that consumes it. Check the installed copy under
  // node_modules, not the release page — the publish workflow checks out
  // `ref: 'main'` rather than the tag.
  memoizeItems: '{{globals.catalog.memoizeItems}}' as any,
  styles: {
    '& .HeadingWrapper': {
      paddingTop: '4px',
    },
    '& .itemCard-warning-wrapper': {
      padding: '10px',
      alignItems: 'flex-start',
    },
  },
  hideOutOfStock:
    '{{default.sessionData.configuration.stock.hideOutOfStock}}' as any,
}

export const CatalogueListDefault = deepMerge(CatalogueListBase, {
  categoryEnterActions: {
    cinnamonActions: [
      {
        name: TemplateActionNames.CATALOG_UPDATE_CATEGORY,
        args: {
          category: '{{this.newValue}}',
        },
      },
      {
        name: TemplateActionNames.CATALOG_UPDATE_CATEGORY_CATALOG,
        args: {
          category: '{{this.newValue}}',
        },
      },
    ],
  } as any,
  runOnLoad: {
    cinnamonActions: [
      {
        name: CinnamonActionNames.CONDITIONAL_PUSH_VIEW,
        args: {
          path: CinnamonPaths.CATEGORIES,
          values: [
            {
              value1: '{{default.config.options.usePaginatedCategories}}',
              value2: true,
            },
          ],
        },
      },
    ],
  },
  viewObserver: {
    showLoader: false,
    entryActions: {
      cinnamonActions: [
        {
          name: TemplateActionNames.CATEGORIES_LAZY_GET_PRODUCTS,
          args: {
            categoryName: '{{this.data.category}}',
          },
        },
      ],
    } as any,
  },
})

export const CatalogueListCategoriesNav = deepMerge(CatalogueListBase, {
  catalogue: '{{categoriesNav.categories}}' as any,
  isFetchingMore: '{{categoriesNav.fetchingMore}}' as any,
  status: '{{categoriesNav.categoryStatus}}' as any,
  runOnLoad: {
    cinnamonActions: [
      {
        name: TemplateActionNames.CATEGORIES_NAVIGATION_GET_PRODUCTS,
      },
    ],
  } as any,
  viewObserver: {
    showLoader: false,
    entryActions: {
      cinnamonActions: [
        {
          name: TemplateActionNames.CATEGORIES_NAVIGATION_GET_PRODUCTS,
          args: {
            categoryName: '{{this.data.category}}',
          },
        },
      ],
    } as any,
  },
})

export const OptionalCatalogueListCategoriesNav = deepMerge(
  CatalogueListCategoriesNav,
  {
    hidden: '{{config.options.hideSubCategoriesProducts}}' as any,
    runOnLoad: {
      cinnamonActions: [
        {
          name: TemplateActionNames.CATEGORIES_NAVIGATION_GET_PRODUCTS,
          args: {
            categoryName: '{{params.categoryName}}',
          },
        },
      ],
    } as any,
    viewObserver: null as any,
  },
)

export const CatalogueListMultiImages = deepMerge(CatalogueListDefault, {
  itemCard: ItemCardCatalogMultiImages,
})
